import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, Minus, Pause, Play, Plus } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { ChordDiagram } from '@/components/ChordDiagram';
import { LevelWave } from '@/components/LevelWave';
import { MicGate } from '@/components/MicGate';
import { Button, Card, SectionTitle } from '@/components/ui';
import { BPM_RANGE } from '@/config/audio';
import { getChord } from '@/data/chords';
import { EIGHTH_LABELS, STRUM_PATTERNS } from '@/data/strumPatterns';
import { t } from '@/i18n';
import { audioEngine } from '@/audio/engine/AudioEngine';
import { useAudioLifecycle, useAudioState, useEngineStore } from '@/hooks/useAudio';
import { useFinishSession } from '@/hooks/useFinishSession';
import { StrummingEngine, type StrummingSettings } from '@/training/strumming/StrummingEngine';
import type { StrumPattern } from '@/types/training';
import './StrummingPage.css';

/**
 * Strumming trainer.
 *
 * The arrows tell the hand what to do; the analyser only checks that a stroke
 * happened and when. That limitation is printed on the screen rather than
 * hidden, because a product that quietly guesses direction would be lying.
 */
export function StrummingPage() {
  const navigate = useNavigate();
  const audio = useAudioState();

  const [settings, setSettings] = useState<StrummingSettings>({
    patternId: STRUM_PATTERNS[0].id,
    chordId: 'Em',
    bpm: 60,
    durationMin: 3,
    clickEnabled: true,
  });
  const [running, setRunning] = useState(false);
  const engineRef = useRef<StrummingEngine | null>(null);
  const [engine, setEngine] = useState<StrummingEngine | null>(null);

  const pattern = STRUM_PATTERNS.find((p) => p.id === settings.patternId) ?? STRUM_PATTERNS[0];

  const handleFinished = useFinishSession();

  useEffect(() => {
    if (!running || audio.status !== 'ready' || engineRef.current) return;
    const created = new StrummingEngine(audioEngine, pattern, settings, handleFinished);
    engineRef.current = created;
    setEngine(created);
    created.start();
    return () => {
      created.dispose();
      engineRef.current = null;
    };
  }, [running, audio.status, pattern, settings, handleFinished]);

  useAudioLifecycle(
    useCallback(() => {
      engineRef.current?.pause();
    }, []),
  );

  const chord = getChord(settings.chordId);

  return (
    <AppShell
      title={t('strum.title')}
      showBack
      // Leaving mid-exercise finishes it, so the practice already done is saved.
      onBack={() => (engineRef.current ? engineRef.current.finish() : navigate(-1))}
    >
      <MicGate>
        {running && engine ? (
          <StrummingSession engine={engine} pattern={pattern} />
        ) : (
          <>
            <Card className="strum__brief">
              <p className="strum__brief-text">{t('strum.instruction')}</p>
            </Card>

            <section className="page-section">
              <SectionTitle>{t('setup.pattern')}</SectionTitle>
              <div className="stack-12">
                {STRUM_PATTERNS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="strum__pattern"
                    aria-pressed={item.id === settings.patternId}
                    onClick={() => setSettings((s) => ({ ...s, patternId: item.id }))}
                  >
                    <span className="strum__pattern-name">{item.nameRu}</span>
                    <PatternArrows pattern={item} />
                  </button>
                ))}
              </div>
            </section>

            <section className="page-section">
              <SectionTitle>{t('setup.chord')}</SectionTitle>
              <div className="strum__chords">
                {['Em', 'Am', 'G', 'C'].map((chordId) => (
                  <button
                    key={chordId}
                    type="button"
                    className="chip"
                    aria-pressed={chordId === settings.chordId}
                    onClick={() => setSettings((s) => ({ ...s, chordId }))}
                  >
                    {chordId}
                  </button>
                ))}
              </div>
            </section>

            <section className="page-section">
              <div className="setup__label-row">
                <SectionTitle>{t('setup.tempo')}</SectionTitle>
                <span className="setup__value tnum">{t('setup.bpm', { value: settings.bpm })}</span>
              </div>
              <div className="slider-row">
                <button
                  type="button"
                  className="btn btn--icon"
                  onClick={() => setSettings((s) => ({ ...s, bpm: Math.max(BPM_RANGE.min, s.bpm - 5) }))}
                  aria-label={t('setup.tempoDown')}
                >
                  <Minus size={20} aria-hidden="true" />
                </button>
                <input
                  className="slider"
                  type="range"
                  min={BPM_RANGE.min}
                  max={BPM_RANGE.max}
                  value={settings.bpm}
                  onChange={(event) => setSettings((s) => ({ ...s, bpm: Number(event.target.value) }))}
                  aria-label={t('setup.tempo')}
                  aria-valuetext={t('setup.bpm', { value: settings.bpm })}
                  style={
                    {
                      '--fill': `${((settings.bpm - BPM_RANGE.min) / (BPM_RANGE.max - BPM_RANGE.min)) * 100}%`,
                    } as React.CSSProperties
                  }
                />
                <button
                  type="button"
                  className="btn btn--icon"
                  onClick={() => setSettings((s) => ({ ...s, bpm: Math.min(BPM_RANGE.max, s.bpm + 5) }))}
                  aria-label={t('setup.tempoUp')}
                >
                  <Plus size={20} aria-hidden="true" />
                </button>
              </div>
            </section>

            {chord && (
              <div className="strum__preview">
                <ChordDiagram chord={chord} size={150} />
              </div>
            )}

            <Button large block onClick={() => setRunning(true)}>
              <Play size={20} fill="currentColor" aria-hidden="true" />
              {t('setup.start')}
            </Button>

            <p className="rhythm__note">{t('strum.directionNote')}</p>
          </>
        )}
      </MicGate>
    </AppShell>
  );
}

function StrummingSession({ engine, pattern }: { engine: StrummingEngine; pattern: StrumPattern }) {
  const snapshot = useEngineStore(engine.store);
  const paused = snapshot.phase === 'paused';

  return (
    <>
      <Card section className="strum__stage">
        {snapshot.phase === 'countIn' ? (
          <div className="active__countin" aria-live="polite">
            <span className="active__countin-number tnum">{snapshot.countInBeat}</span>
            <span className="active__countin-label">{t('active.countIn')}</span>
          </div>
        ) : (
          <>
            <div className="strum__grid" role="group" aria-label={pattern.nameRu}>
              {EIGHTH_LABELS.map((label, eighth) => {
                const step = pattern.steps.find((s) => s.eighth === eighth);
                const active = snapshot.currentEighth === eighth;
                return (
                  <span
                    key={eighth}
                    className={`strum__cell${active ? ' strum__cell--active' : ''}${
                      step ? '' : ' strum__cell--rest'
                    }`}
                  >
                    <span className="strum__arrow" aria-hidden="true">
                      {step ? (
                        step.direction === 'down' ? (
                          <ArrowDown size={34} strokeWidth={2.6} />
                        ) : (
                          <ArrowUp size={34} strokeWidth={2.6} />
                        )
                      ) : (
                        <span className="strum__dash" />
                      )}
                    </span>
                    <span className="strum__count">{label}</span>
                  </span>
                );
              })}
            </div>
            <p className="active__bpm tnum">{t('setup.bpm', { value: snapshot.bpm })}</p>
          </>
        )}
      </Card>

      <div className="rhythm__result" aria-live="polite">
        {snapshot.lastVerdict && (
          <span className={`rhythm__verdict rhythm__verdict--${snapshot.lastVerdict}`}>
            {t(`rhythm.${snapshot.lastVerdict}`)}
          </span>
        )}
        <span className="rhythm__score tnum">
          {t('rhythm.score')} {snapshot.score}%
        </span>
      </div>

      <div className="listening listening--on">
        <LevelWave bars={30} height={26} color="var(--accent)" paused={paused} />
      </div>

      <div className="active__actions">
        <Button variant="secondary" onClick={() => (paused ? engine.resume() : engine.pause())}>
          {paused ? <Play size={18} aria-hidden="true" /> : <Pause size={18} aria-hidden="true" />}
          {paused ? t('active.resume') : t('active.pause')}
        </Button>
        <Button onClick={() => engine.finish()}>{t('active.finish')}</Button>
      </div>

      <p className="rhythm__note">{t('strum.directionNote')}</p>
    </>
  );
}

function PatternArrows({ pattern }: { pattern: StrumPattern }) {
  return (
    <span className="strum__pattern-arrows" aria-hidden="true">
      {EIGHTH_LABELS.map((_, eighth) => {
        const step = pattern.steps.find((s) => s.eighth === eighth);
        if (!step) return <span key={eighth} className="strum__dash strum__dash--small" />;
        return step.direction === 'down' ? (
          <ArrowDown key={eighth} size={18} strokeWidth={2.6} />
        ) : (
          <ArrowUp key={eighth} size={18} strokeWidth={2.6} />
        );
      })}
    </span>
  );
}
